import React, { useCallback, useRef, useState } from 'react';

import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { useAsset, ImageAsset } from '~/techstyle-shared/react-assets';
import {
  mobile,
  Carousel as BaseCarousel,
  useLayoutEffect,
  useBreakpoint,
  Video,
  desktop,
} from '~/techstyle-shared/react-components';

import {
  parseMediaPlayerConfigFromAsset,
  parseVideoIdInput,
} from '../../utils/assetHelper';
import parsePositionString, { Position } from '../../utils/parsePositionString';
import usePromoTracking from '../../utils/usePromoTracking';
import { SplitScreenHeroBanner } from '../SplitScreenHeroBanner';
import TextNode from '../TextNode';
import TrackedAssetContainer from '../TrackedAssetContainer';

const Wrapper = styled.div`
  position: relative;
  isolation: isolate;
  display: flex;
  overflow: hidden;

  /* Provide a background to read the white text on before the asset loads in */
  background: ${({ theme }) => theme.fullWidthCarousel}

  ${mobile`
    min-height: 325px;
  `}

  --carousel-image-aspect-ratio: ${(p) =>
    p.$assetWidthDesktop !== 0
      ? `calc(${p.$assetHeightDesktop} / ${p.$assetWidthDesktop})`
      : '0'};
  ${mobile`
    --carousel-image-aspect-ratio: ${(p) =>
      p.$assetWidthMobile !== 0
        ? `calc(${p.$assetHeightMobile} / ${p.$assetWidthMobile})`
        : '0'};
  `}

  /*
   * A CSS hack to keep the aspect ratio of the container equal
   * to that of the asset when the viewport is wide.
   */
  &::before {
    content: '';
    padding-bottom: calc(100% * var(--carousel-image-aspect-ratio));
  }
`;

const Overlay = styled.div`
  color: ${({ theme }) => theme.colors.textLight};
  position: absolute;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-self: stretch;
  justify-self: stretch;
  padding: ${({ theme: { sizes } }) => `${sizes.md}px ${sizes.xxl}px`};

  ${({ $overlayPositionVertical }) =>
    ({
      [Position.TOP]: css`
        justify-content: flex-start;
      `,
      [Position.CENTER]: css`
        justify-content: center;
      `,
      [Position.BOTTOM]: css`
        justify-content: flex-end;
      `,
    }[$overlayPositionVertical])};
  ${({ $overlayPositionHorizontal }) =>
    ({
      [Position.LEFT]: css`
        text-align: left;
        align-items: flex-start;
      `,
      [Position.CENTER]: css`
        text-align: center;
        align-items: center;
      `,
      [Position.RIGHT]: css`
        text-align: right;
        align-items: flex-end;
      `,
    }[$overlayPositionHorizontal])};

  /*
   * Disable mouse interaction on this element to allow the user to interact
   * freely with the carousel in the background, but reenable pointer events for
   * any interactive child elements (not accounting for nonstandard implementations).
   */
  pointer-events: none;
  button,
  a,
  input,
  textarea,
  select,
  label {
    pointer-events: auto;
  }

  ${mobile`
    max-width: 100%;
    padding: ${({ theme: { sizes } }) => `${sizes.xs}px`};
  `}
`;
const RESIZE_MODE_ATTR = 'data-carousel-resize-mode';
const cellStyle = css`
  width: 100%;
  min-height: 100%;
  display: flex;
  align-items: center;

  [${RESIZE_MODE_ATTR}] & {
    min-height: 0;
  }
`;

const Carousel = styled(BaseCarousel)`
  && {
    position: absolute;
  }
  top: 50%;
  transform: translateY(-50%);
  width: 100%;

  ${mobile`
    min-height: 325px;
  `}

  .flickity-button.flickity-button {
    background: transparent;
  }

  .flickity-button {
    color: ${({ theme: { colors } }) => colors.textLight};
    ${({ theme }) => theme.carouselButtonAccessibilityStyles};
  }
  .flickity-button-icon.flickity-button-icon {
    width: 40%;
    height: 40%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

const StyledImageAsset = styled(ImageAsset)`
  width: 100%;
  img {
    ${desktop`
      width: 100%;
    `}
    ${mobile`
      min-height: 325px;
      object-fit: cover;
    `}
  }
`;

const ImageAssetOverlay = styled.div`
  position: absolute;
  bottom: 24px;
  left: 40px;
  ${mobile`
		bottom: 12px;
		left: 12px;
	`}
  color: ${({ theme: { colors } }) => colors.textLight};
`;

const Slide = styled.div`
  position: relative;
  width: 100%;
`;

const Title = styled(TextNode)`
  font-size: 49px;
  font-weight: 800;
`;

const Description = styled(TextNode)`
  font-size: 26px;
  font-weight: bold;
  margin-bottom: 16px;
`;

const VideoWithNoPointerEvents = styled(Video)`
  pointer-events: none;
`;

/**
 * A carousel component built for the use case of a full-width carousel.
 *
 * When asset dimension props are not specified, the dimensions from the
 * container itself are used. If the container is preloaded, the user will see
 * no content shifts. If the container does not have dimensions specified, the
 * first image loaded will be used as a reference for the dimensions of all
 * other assets. Until the container or first image loads, this component will
 * match its height to that of the overlay contents, if there are any.
 *
 * The change in height that occurs when the container or image loads will cause
 * content shift, so it is strongly recommended to preload the container or
 * specify `assetHeight` and `assetWidth` for a smooth viewing experience.
 */
export default function FullWidthCarousel({
  containerDesktop,
  containerMobile,
  children,
  overlayPosition: overlayPositionRaw,
  rbStyles,
  autoPlay = true,
  ...otherProps
}) {
  const { triggerPromoTracking, onPromoViewed } = usePromoTracking();
  const flickityRef = useRef();
  const wrapperRef = useRef(null);
  const { isMobile } = useBreakpoint();
  const [detectedDimensions, setDetectedDimensions] = useState({
    height: 0,
    width: 0,
  });
  const [overlayPositionVertical, overlayPositionHorizontal] =
    parsePositionString(overlayPositionRaw) || [
      Position.CENTER,
      Position.CENTER,
    ];

  const useContainerWithDimensions = (container, type) => {
    const assetContainer = useAsset(container, { multi: true });
    const {
      assets = [],
      height: containerHeight,
      width: containerWidth,
    } = assetContainer?.data || {};

    const height =
      otherProps[`assetHeight${type}`] ??
      containerHeight ??
      detectedDimensions.height;
    const width =
      otherProps[`assetWidth${type}`] ??
      containerWidth ??
      detectedDimensions.width;

    return { height, width, assets };
  };

  const {
    assets: assetsDesktop,
    width: assetWidthDesktop,
    height: assetHeightDesktop,
  } = useContainerWithDimensions(containerDesktop, 'Desktop');

  const {
    assets: assetsMobile,
    width: assetWidthMobile,
    height: assetHeightMobile,
  } = useContainerWithDimensions(containerMobile, 'Mobile');

  const assets =
    (isMobile && containerMobile) || !containerDesktop
      ? assetsMobile
      : assetsDesktop;

  const flickityRefCallback = useCallback(
    (flickity) => {
      if (flickity !== null) {
        flickityRef.current = flickity;

        // fire for the first asset
        if (assets.length && assets[0].id) {
          onPromoViewed({ itemInfo: assets[0], id: assets[0].id });
        }

        // autoPlay carousel again after moving forward or back
        flickity.on('change', (index) => {
          if (assets[index] && assets[index].id) {
            onPromoViewed({ itemInfo: assets[index], id: assets[index].id });
          }
          flickity.player.play();
        });

        // We add to the default resize function on this flickity instance, adding
        // some extra logic to manage the automatic height adjustment demonstrated
        // by the Flickity author in this demo:
        // https://codepen.io/desandro/pen/ZXEGVq?editors=0110
        const originalResize = flickity.resize;
        flickity.resize = function () {
          if (wrapperRef.current) {
            wrapperRef.current.setAttribute(RESIZE_MODE_ATTR, '');
          }
          originalResize.call(this);
          if (wrapperRef.current) {
            wrapperRef.current.removeAttribute(RESIZE_MODE_ATTR);
          }
        };

        // At the time flickity is activated, the img elements will already be in
        // the DOM, so we take advantage of this timing to give them some onLoad
        // listeners that the ImageAsset component doesn't currently have a prop
        // for.
        const images = wrapperRef.current
          ? wrapperRef.current.querySelectorAll('img')
          : [];
        // We assume that every image in the carousel uses the same dimensions, so
        // we listen for the loading of the first image and then save its dimensions
        const getDimensionsFromFirstImageLoad = function () {
          images.forEach((image) =>
            image.removeEventListener('load', getDimensionsFromFirstImageLoad)
          );

          const { naturalHeight: height, naturalWidth: width } = this;
          setDetectedDimensions({ height, width });
        };
        images.forEach((image) =>
          image.addEventListener('load', getDimensionsFromFirstImageLoad)
        );
      }
    },
    [assets, onPromoViewed]
  );

  const debouncedFlickityReset = debounce(() => {
    flickityRefCallback(flickityRef.current);
  }, 100);

  useLayoutEffect(() => {
    if (flickityRef.current && detectedDimensions.width > 0) {
      flickityRef.current.resize();
    }
  }, [detectedDimensions]);

  let prevNextButtons = false;
  if (assets.length > 1) {
    prevNextButtons = true;
  }

  const sortByPosition = (firstAsset, succeedingAsset) => {
    const { position: firstAssetPos = -1 } = firstAsset?.options?.customVars;
    const { position: succAssetPos = -1 } =
      succeedingAsset?.options?.customVars;
    if (firstAssetPos === -1) {
      return -1;
    }
    if (firstAssetPos === succAssetPos) {
      return 0;
    }
    return firstAssetPos - succAssetPos;
  };

  if (assets[0]?.options?.customVars?.position) {
    assets.sort(sortByPosition);
  }

  const renderVideo = ({ aspectRatio, url, config, videoTargetUrl }) => {
    if (videoTargetUrl) {
      return (
        <a href={videoTargetUrl}>
          <VideoWithNoPointerEvents
            aspectRatio={aspectRatio}
            url={url}
            config={config}
          />
        </a>
      );
    }
    return <Video aspectRatio={aspectRatio} url={url} config={config} />;
  };

  const renderAsset = (asset, assetIndex) => {
    const {
      title_rb_header_default: titleRbHeaderDefault,
      title_rb_header: titleRbHeader,
      title_rb_subheader_default: titleRbSubheaderDefault,
      title_rb_subheader: titleRbSubheader,
      splitVideoId,
    } = asset.options.customVars;

    let defaultVideoAspectRatio;
    if (splitVideoId) {
      defaultVideoAspectRatio = 16 / 9;
    }
    // check for a video id
    const { videoId, videoAspectRatio, videoTargetUrl } = parseVideoIdInput({
      asset,
      defaultVideoAspectRatio,
    });
    const useEmbeddedRbs = !!titleRbHeaderDefault || !!titleRbSubheader;
    const isVideoOnly = videoId && !splitVideoId;
    const isImageOnly = !videoId && !splitVideoId;
    const isSplitVideo = !!splitVideoId;

    return (
      <Slide key={assetIndex}>
        {isVideoOnly &&
          renderVideo({
            aspectRatio: videoAspectRatio,
            url: Video.getUrlFromVideoId(videoId),
            config: parseMediaPlayerConfigFromAsset({ asset }),
            videoTargetUrl,
          })}
        {isSplitVideo && (
          <SplitScreenHeroBanner
            key={`${isMobile ? 'mobile' : 'desktop'}-${asset.id}`}
            videoAspectRatio={videoAspectRatio}
            videoId={splitVideoId}
            asset={asset}
            parentRefs={{ wrapperRef, debouncedFlickityReset }}
          />
        )}
        {isImageOnly && (
          <StyledImageAsset
            asset={asset}
            key={assetIndex}
            onClick={() => triggerPromoTracking(asset)}
          />
        )}
        {useEmbeddedRbs && (
          <ImageAssetOverlay>
            {titleRbHeaderDefault && (
              <Title
                defaultMessage={titleRbHeaderDefault}
                messageId={titleRbHeader}
                containerStyle={rbStyles?.title?.container_style}
                textStyle={rbStyles?.title?.text_style}
              />
            )}
            {titleRbSubheader && (
              <Description
                defaultMessage={titleRbSubheaderDefault}
                messageId={titleRbSubheader}
                containerStyle={rbStyles?.description?.container_style}
                textStyle={rbStyles?.description?.text_style}
              />
            )}
          </ImageAssetOverlay>
        )}
      </Slide>
    );
  };

  return (
    <Wrapper
      ref={wrapperRef}
      $assetHeightDesktop={assetHeightDesktop}
      $assetWidthDesktop={assetWidthDesktop}
      $assetHeightMobile={assetHeightMobile}
      $assetWidthMobile={assetWidthMobile}
      {...otherProps}
    >
      {assets.length > 0 && (
        <TrackedAssetContainer asset={assets[0]}>
          <Carousel
            flickityRef={flickityRefCallback}
            pageDots={false}
            autoPlay={autoPlay}
            wrapAround
            cellStyle={cellStyle}
            prevNextButtons={prevNextButtons}
          >
            {assets.map((asset, assetIndex) => renderAsset(asset, assetIndex))}
          </Carousel>
        </TrackedAssetContainer>
      )}
      {children && (
        <Overlay
          $overlayPositionVertical={overlayPositionVertical}
          $overlayPositionHorizontal={overlayPositionHorizontal}
        >
          {children}
        </Overlay>
      )}
    </Wrapper>
  );
}

FullWidthCarousel.propTypes = {
  /**
   * The natural height of the desktop image assets provided to the carousel. We
   * assume that all assets have the same dimensions.
   */
  assetHeightDesktop: PropTypes.number,
  /**
   * The natural height of the mobile image assets provided to the carousel. We
   * assume that all assets have the same dimensions.
   */
  assetHeightMobile: PropTypes.number,
  /**
   * The natural width of the desktop image assets provided to the carousel. We
   * assume that all assets have the same dimensions.
   */
  assetWidthDesktop: PropTypes.number,
  /**
   * The natural width of the mobile image assets provided to the carousel. We
   * assume that all assets have the same dimensions.
   */
  assetWidthMobile: PropTypes.number,
  /**
   Interval of images
   * autoPlay enables playing of videos when the video is on screen,
   * it can be also set to a number which is measured in ms(miliseconds)
   */
  autoPlay: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  /**
   * Contents to display in an overlay superimposed over the carousel.
   */
  children: PropTypes.node,
  /**
   * Name of the asset container in which the carousel images for use on desktop
   * can be found.
   *
   * NOTE: Only one container name may be specified at a time. If the carousel
   * assets are located in multiple different containers, they must be
   * consolidated into a new container on the CMS.
   */
  containerDesktop: PropTypes.string,
  /**
   * Name of the asset container in which the carousel images for use on mobile
   * can be found.
   *
   * NOTE: Only one container name may be specified at a time. If the carousel
   * assets are located in multiple different containers, they must be
   * consolidated into a new container on the CMS.
   */
  containerMobile: PropTypes.string,

  overlayPosition: (props, propName, componentName) => {
    if (
      props[propName] !== undefined &&
      parsePositionString(props[propName]) === undefined
    ) {
      return new Error(
        `Invalid prop "${propName}" supplied to ${componentName}. Validation failed.`
      );
    }
  },

  rbStyles: PropTypes.object,
};
